import React, { useState, useEffect, useRef } from "react";
import DeckGL from "@deck.gl/react";
import { ScatterplotLayer } from "@deck.gl/layers";
import { HexagonLayer } from "@deck.gl/aggregation-layers";
import { PolygonLayer } from "@deck.gl/layers";
import { Map } from "react-map-gl/maplibre";
import MapAttribution from "./MapAttribution";
import { MapGeometry } from "../../services/mapsApi";
import { useThemeColors } from "../../hooks/useThemeColors";
import { basemapStyle } from "../../config/theme";
import { COUNTRY_LIST } from "../../config/countries";
import type { MapView } from "../../config/countries";
import { fitView, outlineRings, readShape, readShapes } from "../../geo/areaShapes";

interface MapData {
  position: [number, number];
  population: number;
  name: string;
}

export interface MapClick {
  lng: number;
  lat: number;
}

export interface MapMarker {
  id: string;
  /** [lng, lat] */
  position: [number, number];
}

interface MapProps {
  data?: MapData[];
  selectedPoints?: MapData[];
  onPointClick?: (info: any) => void;
  onViewStateChange?: (viewState: any) => void;
  viewState?: any;
  geometries?: MapGeometry[];
  loading?: boolean;
  selectedGrids?: MapGeometry[];
  onGridClick?: (info: any) => void;
  /**
   * Start view of the uncontrolled map; defaults to the first registry country's. Applies on
   * mount only; pass `key` to recentre on a new country.
   */
  initialView?: MapView;
  /** Called with the clicked coordinate when the click lands on the map. */
  onMapClick?: (p: MapClick) => void;
  markers?: MapMarker[];
  /**
   * Set when the outline of the areas could not be loaded: the map stays on its start view and says so,
   * with a way to ask again.
   */
  outlineError?: { onRetry: () => void };
}

const NO_POINTS: MapData[] = [];
const noop = () => undefined;
const crosshairCursor = ({ isDragging }: { isDragging: boolean }) =>
  isDragging ? "grabbing" : "crosshair";

const MapComponent: React.FC<MapProps> = ({
  data = NO_POINTS,
  selectedPoints = NO_POINTS,
  onPointClick = noop,
  onViewStateChange,
  viewState: externalViewState,
  geometries = [],
  loading = false,
  selectedGrids = [],
  onGridClick,
  initialView,
  onMapClick,
  markers,
  outlineError,
}) => {
  const colors = useThemeColors();
  const isDark = colors.dark;
  // Marker dot: the dark-mode-aware brand shade with a white outline. Memoised so deck.gl
  // sees the same array until the theme flips.
  const markerFill = React.useMemo(() => colors.rgb("brand-700"), [colors]);
  const markerOutline = React.useMemo(() => colors.rgb("on-brand"), [colors]);
  const initialViewState = React.useMemo(
    () => ({
      ...(initialView ?? COUNTRY_LIST[0].defaultView),
      pitch: 0,
      bearing: 0,
    }),
    [initialView]
  );
  const [internalViewState, setInternalViewState] =
    useState(initialViewState);
  const viewState = externalViewState || internalViewState;
  const hasAutoZoomedRef = useRef(false);

  // Auto-zoom to the selected grids when they first load
  useEffect(() => {
    if (selectedGrids.length > 0 && !hasAutoZoomedRef.current && !externalViewState) {
      const shapes = readShapes(selectedGrids.map((g) => g.geometry));
      if (shapes.length > 0) {
        const { longitude, latitude, zoom } = fitView(shapes, null);
        setInternalViewState({ longitude, latitude, zoom, pitch: 0, bearing: 0 });
        hasAutoZoomedRef.current = true;
      }
    }
  }, [selectedGrids, externalViewState]);

  // Combine current geometries with selected grids (ensuring selected grids are always shown)
  const allGeometries = React.useMemo(() => {
    const geometryById: Record<string, MapGeometry> = {};

    // Add current geometries
    geometries.forEach((geom, index) => {
      // Use index as a fallback key if id is missing, to prevent overwriting.
      const key = geom.geo_id ?? `no-id-${index}`;
      geometryById[key] = geom;
    });

    // Add selected grids (overwrite if they exist in current geometries)
    selectedGrids.forEach((grid) => {
      if (grid.geo_id) {
        // Ensure grid.id exists before using it as a key
        geometryById[grid.geo_id] = grid;
      }
    });

    return Object.values(geometryById);
  }, [geometries, selectedGrids]);

  // One renderable polygon per outline ring (a polygon's holes, the islands of a multipolygon, a road link's line).
  const polygonData = React.useMemo(
    () =>
      allGeometries.flatMap((geom) => {
        const shape = readShape(geom.geometry);
        if (!shape) return [];
        return outlineRings(shape).map((ring) => ({
          id: geom.geo_id,
          polygon: ring,
          color: (geom as any).color,
        }));
      }),
    [allGeometries],
  );

  const layers = [
    new ScatterplotLayer({
      id: "scatterplot-layer",
      data: data,
      pickable: true,
      opacity: 0.8,
      stroked: true,
      filled: true,
      radiusScale: 6,
      radiusMinPixels: 8,
      radiusMaxPixels: 40,
      lineWidthMinPixels: 1,
      getPosition: (d: any) => d.position,
      getRadius: (d: any) => Math.sqrt(d.population / 10000),
      getFillColor: (d: any) =>
        selectedPoints.find((p) => p.name === d.name)
          ? [255, 100, 100, 200]
          : [100, 150, 255, 200],
      getLineColor: [255, 255, 255],
      onClick: onPointClick,
      updateTriggers: {
        getFillColor: selectedPoints,
      },
    }),

    new HexagonLayer({
      id: "hexagon-layer",
      data: data,
      pickable: true,
      extruded: true,
      radius: 50000,
      elevationScale: 4,
      getPosition: (d: any) => d.position,
      getFillColor: [100, 255, 150, 120],
      getElevation: (d: any) => d.population / 100000,
      opacity: 0.3,
    }),

    // Polygon layer for geometries
    ...(polygonData.length > 0
      ? [
          new PolygonLayer({
            id: "polygon-layer",
            data: polygonData,
            pickable: true,
            stroked: true,
            filled: true,
            wireframe: false,
            lineWidthMinPixels: 1,
            getPolygon: (d: any) => d.polygon,
            getFillColor: (d: any) => {
              if (selectedGrids.some((grid) => grid.geo_id === d.id)) {
                return [0, 255, 0, 150]; // Green for selected grids
              }
              if (d.color === "blue") {
                return [0, 100, 255, 100]; // Blue for origin areas
              }
              if (d.color === "red") {
                return [255, 50, 50, 100]; // Red for destination areas
              }
              if (d.color === "purple") {
                return [128, 0, 128, 100]; // Purple for areas that are both origin and destination
              }
              return [0, 0, 0, 0]; // Transparent for unselected grids
            },
            getLineColor: (d: any) => {
              if (selectedGrids.some((grid) => grid.geo_id === d.id)) {
                return [0, 200, 0, 255]; // Dark green border for selected
              }
              if (d.color === "blue") {
                return [0, 50, 200, 255]; // Dark blue border for origin areas
              }
              if (d.color === "red") {
                return [200, 20, 20, 255]; // Dark red border for destination areas
              }
              if (d.color === "purple") {
                return [100, 0, 100, 255]; // Dark purple border for both origin and destination
              }
              return [255, 140, 0, 255]; // Dark orange border for unselected
            },
            getLineWidth: (d: any) =>
              selectedGrids.some((grid) => grid.geo_id === d.id) ? 3 : 2, // Thicker border for selected
            onClick: onGridClick,
            updateTriggers: {
              getFillColor: [selectedGrids, allGeometries],
              getLineColor: [selectedGrids, allGeometries],
              getLineWidth: selectedGrids,
            },
          }),
        ]
      : []),

    ...(markers?.length
      ? [
          new ScatterplotLayer({
            id: "map-markers",
            data: markers,
            pickable: false,
            stroked: true,
            filled: true,
            radiusUnits: "pixels",
            getRadius: 7,
            lineWidthMinPixels: 2,
            getPosition: (d: MapMarker) => d.position,
            getFillColor: markerFill,
            getLineColor: markerOutline,
          }),
        ]
      : []),
  ];

  return (
    <div className="w-full h-full relative">
      <DeckGL
        viewState={viewState}
        onViewStateChange={({ viewState }) => {
          if (!externalViewState) {
            setInternalViewState(viewState as any);
          }
          onViewStateChange?.(viewState);
        }}
        controller={true}
        layers={layers}
        onClick={(info: any) => {
          if (onMapClick && info?.coordinate) {
            onMapClick({ lng: info.coordinate[0], lat: info.coordinate[1] });
          }
        }}
        // Spread, not `getCursor={undefined}`: Deck merges props over its defaults, so an explicit
        // undefined would replace the default cursor callback and throw on every frame.
        {...(onMapClick ? { getCursor: crosshairCursor } : {})}
        style={{ width: "100%", height: "100%" }}
      >
        <Map
          initialViewState={initialViewState}
          mapStyle={basemapStyle(isDark)}
          attributionControl={false}
          reuseMaps
        />
      </DeckGL>

      {outlineError && (
        <div
          role="alert"
          className="absolute left-3 top-3 flex items-center gap-3 rounded-lg border border-paper-300 bg-surface px-3 py-2 text-sm text-ink shadow"
        >
          <span>Couldn't load the area outline</span>
          <button type="button" className="font-medium text-brand-700 underline" onClick={outlineError.onRetry}>
            Retry
          </button>
        </div>
      )}

      <MapAttribution />
    </div>
  );
};

export default MapComponent;
